import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, TrendingUp, TrendingDown, Wallet, Camera, Eye, EyeOff, ChevronDown, ChevronUp, Trash2, X, Receipt, BadgeDollarSign, FileText, Edit2, Calendar } from 'lucide-react';
import { format, subDays, subMonths, subYears, startOfWeek, startOfMonth, startOfYear, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import MileageTracker from './MileageTracker';

// Helper to extract error message from API errors (handles FastAPI validation errors)
const getErrorMessage = (error, defaultMessage = 'An error occurred') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return defaultMessage;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return defaultMessage;
};

export default function Home({ user }) {
  // Mobile detection utility
  const isMobile = () => {
    return window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Tax Year selector - default to current year
  const currentYear = new Date().getFullYear();
  const [selectedTaxYear, setSelectedTaxYear] = useState(currentYear.toString());
  const availableTaxYears = [
    currentYear.toString(),
    (currentYear - 1).toString(),
    (currentYear - 2).toString(),
  ];

  const [summary, setSummary] = useState({ total_income: 0, total_expenses: 0, profit: 0 });
  const [recentIncome, setRecentIncome] = useState([]);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [showIncomeDialog, setShowIncomeDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  // Load visibility preferences from localStorage - default to hidden on mobile, shown on desktop
  const [hideAmounts, setHideAmounts] = useState(() => {
    const saved = localStorage.getItem('hideAmounts');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    // Default to hidden on mobile, shown on desktop
    return isMobile();
  });
  const [hideRecentIncomeAmounts, setHideRecentIncomeAmounts] = useState(() => {
    const saved = localStorage.getItem('hideRecentIncomeAmounts');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    // Default to hidden on mobile, shown on desktop
    return isMobile();
  });
  const [hideRecentExpenseAmounts, setHideRecentExpenseAmounts] = useState(() => {
    const saved = localStorage.getItem('hideRecentExpenseAmounts');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    // Default to hidden on mobile, shown on desktop
    return isMobile();
  });
  const [showRecentIncome, setShowRecentIncome] = useState(false);
  const [showRecentExpenses, setShowRecentExpenses] = useState(false);
  
  // Financial Summary - collapsed by default on mobile, expanded on desktop
  const [showFinancialSummary, setShowFinancialSummary] = useState(() => {
    return !isMobile(); // Collapsed on mobile, expanded on desktop
  });
  
  // Receipt viewer state
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [currentReceipt, setCurrentReceipt] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  
  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'income'|'expense', id: string }
  
  // Income form state
  const [incomeSources, setIncomeSources] = useState([]);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [incomeFormData, setIncomeFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    source: '',
    category: '',
    payment_method: '',
    amount: '',
    notes: ''
  });
  const [incomeFile, setIncomeFile] = useState(null);

  // Expense form state
  const [expenseVendors, setExpenseVendors] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expenseFormData, setExpenseFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    vendor: '',
    category: '',
    amount: '',
    notes: ''
  });
  const [expenseFile, setExpenseFile] = useState(null);

  // Income with Expenses state
  const [showIncomeWithExpensesDialog, setShowIncomeWithExpensesDialog] = useState(false);
  const [incomeWithExpensesData, setIncomeWithExpensesData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    source: '',
    category: '',
    payment_method: '',
    income_amount: '',
    income_notes: '',
    expenses: []
  });

  // Inline add state
  const [showAddSource, setShowAddSource] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showAddExpenseCategory, setShowAddExpenseCategory] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newVendorCategory, setNewVendorCategory] = useState('');
  
  // Manage dropdown items state
  const [showManageSources, setShowManageSources] = useState(false);
  const [showAddIncomeCategoryDialog, setShowAddIncomeCategoryDialog] = useState(false);
  const [newIncomeCategoryName, setNewIncomeCategoryName] = useState('');
  
  // Chart state
  const [chartPeriod, setChartPeriod] = useState('month');
  const [chartData, setChartData] = useState([]);
  const [topIncomeCategories, setTopIncomeCategories] = useState([]);
  const [topExpenseCategories, setTopExpenseCategories] = useState([]);
  const [chartsLoaded, setChartsLoaded] = useState(false);
  const [newIncomeCategorySource, setNewIncomeCategorySource] = useState('');
  const [showManageIncomeCategories, setShowManageIncomeCategories] = useState(false);
  const [showManagePayments, setShowManagePayments] = useState(false);
  const [showManageVendors, setShowManageVendors] = useState(false);
  const [showManageExpenseCategories, setShowManageExpenseCategories] = useState(false);

  const canEdit = user?.role === 'owner' || user?.permissions?.can_edit_income || user?.permissions?.can_edit_expenses;

  // Toggle functions that save to localStorage
  const toggleHideAmounts = () => {
    const newValue = !hideAmounts;
    setHideAmounts(newValue);
    localStorage.setItem('hideAmounts', JSON.stringify(newValue));
  };

  const toggleHideRecentIncomeAmounts = () => {
    const newValue = !hideRecentIncomeAmounts;
    setHideRecentIncomeAmounts(newValue);
    localStorage.setItem('hideRecentIncomeAmounts', JSON.stringify(newValue));
  };

  const toggleHideRecentExpenseAmounts = () => {
    const newValue = !hideRecentExpenseAmounts;
    setHideRecentExpenseAmounts(newValue);
    localStorage.setItem('hideRecentExpenseAmounts', JSON.stringify(newValue));
  };

  useEffect(() => {
    fetchSummary();
    fetchRecentEntries();
    fetchDropdownData();
  }, [selectedTaxYear]);

  // Process chart data - DISABLED FOR NOW
  // useEffect(() => {
  //   if (!hideData) {
  //     try {
  //       processChartData().catch(err => {
  //         console.error('Chart processing error:', err);
  //         setChartsLoaded(false);
  //       });
  //     } catch (error) {
  //       console.error('Chart processing error:', error);
  //       setChartsLoaded(false);
  //     }
  //   }
  // }, [chartPeriod, hideData]);

  const processChartData = async () => {
    return; // DISABLED
    const now = new Date();
    let startDate;
    let intervals;

    // Determine date range based on period
    if (chartPeriod === 'week') {
      startDate = subDays(now, 7);
      intervals = eachDayOfInterval({ start: startDate, end: now });
    } else if (chartPeriod === 'month') {
      startDate = subMonths(now, 1);
      intervals = eachDayOfInterval({ start: startDate, end: now }).filter((d, i) => i % 3 === 0); // Every 3 days
    } else {
      startDate = subYears(now, 1);
      intervals = eachMonthOfInterval({ start: startDate, end: now });
    }

    // Fetch all income and expenses for the period
    try {
      const [incomeRes, expensesRes] = await Promise.all([
        axios.get(`${API}/income`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
        axios.get(`${API}/expenses`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      ]);

      const allIncome = incomeRes.data;
      const allExpenses = expensesRes.data;

      // Filter data by date range
      const filteredIncome = allIncome.filter(i => new Date(i.date) >= startDate);
      const filteredExpenses = allExpenses.filter(e => new Date(e.date) >= startDate);

      // Process time series data
      const timeSeriesData = intervals.map(date => {
        const dateStr = format(date, chartPeriod === 'year' ? 'MMM yyyy' : 'MMM dd');
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(date);
        dayEnd.setHours(23, 59, 59, 999);

        if (chartPeriod === 'year') {
          // For year view, group by month
          const monthIncome = filteredIncome.filter(i => {
            const d = new Date(i.date);
            return d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
          });
          const monthExpenses = filteredExpenses.filter(e => {
            const d = new Date(e.date);
            return d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
          });

          return {
            name: dateStr,
            income: monthIncome.reduce((sum, i) => sum + i.amount, 0),
            expenses: monthExpenses.reduce((sum, e) => sum + e.amount, 0)
          };
        } else {
          // For week/month view, group by day
          const dayIncome = filteredIncome.filter(i => {
            const d = new Date(i.date);
            return d >= dayStart && d <= dayEnd;
          });
          const dayExpenses = filteredExpenses.filter(e => {
            const d = new Date(e.date);
            return d >= dayStart && d <= dayEnd;
          });

          return {
            name: dateStr,
            income: dayIncome.reduce((sum, i) => sum + i.amount, 0),
            expenses: dayExpenses.reduce((sum, e) => sum + e.amount, 0)
          };
        }
      });

      setChartData(timeSeriesData);

      // Process top categories
      const incomeByCategory = {};
      filteredIncome.forEach(i => {
        const cat = i.category || 'Uncategorized';
        incomeByCategory[cat] = (incomeByCategory[cat] || 0) + i.amount;
      });

      const expenseByCategory = {};
      filteredExpenses.forEach(e => {
        const cat = e.category || 'Uncategorized';
        // Exclude Tax Deposits from top expenses chart
        if (cat !== 'Tax Deposits') {
          expenseByCategory[cat] = (expenseByCategory[cat] || 0) + e.amount;
        }
      });

      // Convert to arrays and sort
      const topIncome = Object.entries(incomeByCategory)
        .map(([category, amount]) => ({ category, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 10);

      const topExpense = Object.entries(expenseByCategory)
        .map(([category, amount]) => ({ category, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 10);

      setTopIncomeCategories(topIncome);
      setTopExpenseCategories(topExpense);
      setChartsLoaded(true);
    } catch (error) {
      console.error('Failed to process chart data:', error);
      setChartsLoaded(false);
    }
  };

  const formatAmount = (amount, shouldHide = hideAmounts) => {
    if (shouldHide) {
      return '••••••';
    }
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleAddSource = async () => {
    if (!newItemName.trim()) {
      toast.error('Please enter a source name');
      return;
    }
    try {
      await axios.post(`${API}/income-sources`, { name: newItemName.trim() });
      toast.success('Income source added');
      setNewItemName('');
      setShowAddSource(false);
      await fetchDropdownData();
      setIncomeFormData({ ...incomeFormData, source: newItemName.trim() });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add source'));
    }
  };

  const handleAddIncomeCategory = async () => {
    const nameToAdd = newIncomeCategoryName.trim() || newItemName.trim();
    if (!nameToAdd) {
      toast.error('Please enter a category name');
      return;
    }
    if (!newIncomeCategorySource) {
      toast.error('Please select a revenue category (type of income)');
      return;
    }
    try {
      await axios.post(`${API}/income-categories`, { 
        name: nameToAdd,
        source: newIncomeCategorySource
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Customer/Source added successfully');
      setShowManageIncomeCategories(false);
      setShowAddIncomeCategoryDialog(false);
      setNewItemName('');
      setNewIncomeCategoryName('');
      setNewIncomeCategorySource('');
      
      // Fetch updated dropdown data first
      await fetchDropdownData();
      
      // Use setTimeout to ensure state update and component re-render after fetch completes
      setTimeout(() => {
        // Auto-select in appropriate form with the source
        // Use functional form to get current state
        if (showIncomeDialog) {
          setIncomeFormData(prev => ({ 
            ...prev, 
            category: nameToAdd,
            source: newIncomeCategorySource
          }));
        } else if (showIncomeWithExpensesDialog) {
          setIncomeWithExpensesData(prev => ({ 
            ...prev, 
            category: nameToAdd,
            source: newIncomeCategorySource 
          }));
        }
      }, 200);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add category'));
    }
  };

  const handleAddPayment = async () => {
    if (!newItemName.trim()) {
      toast.error('Please enter a payment method name');
      return;
    }
    try {
      const paymentName = newItemName.trim();
      await axios.post(`${API}/payment-methods`, { name: paymentName });
      toast.success('Payment method added');
      setNewItemName('');
      setShowAddPayment(false);
      
      // Fetch updated dropdown data first
      await fetchDropdownData();
      
      // Use setTimeout to ensure state update and component re-render after fetch completes
      setTimeout(() => {
        setIncomeFormData(prev => ({ ...prev, payment_method: paymentName }));
      }, 200);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add payment method'));
    }
  };

  const handleAddVendor = async () => {
    if (!newItemName.trim()) {
      toast.error('Please enter a vendor name');
      return;
    }
    if (!newVendorCategory) {
      toast.error('Please select a category for this vendor');
      return;
    }
    try {
      const vendorName = newItemName.trim();
      await axios.post(`${API}/expense-vendors`, { 
        name: vendorName,
        category: newVendorCategory
      });
      toast.success('Vendor added');
      setShowAddVendor(false);
      setShowManageVendors(false);
      setNewItemName('');
      setNewVendorCategory('');
      
      // Fetch updated dropdown data first
      await fetchDropdownData();
      
      // Use setTimeout to ensure state update and component re-render after fetch completes
      setTimeout(() => {
        setExpenseFormData(prev => ({ 
          ...prev, 
          vendor: vendorName,
          category: newVendorCategory  // Also auto-populate the category
        }));
      }, 200);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add vendor'));
    }
  };

  const handleAddExpenseCategory = async () => {
    if (!newItemName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    try {
      const categoryName = newItemName.trim();
      await axios.post(`${API}/expense-categories`, { name: categoryName });
      toast.success('Expense category added');
      setShowAddExpenseCategory(false);
      setShowManageExpenseCategories(false);
      setNewItemName('');
      
      // Fetch updated dropdown data first
      await fetchDropdownData();
      
      // Use setTimeout to ensure state update and component re-render after fetch completes
      setTimeout(() => {
        setExpenseFormData(prev => ({ ...prev, category: categoryName }));
      }, 200);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add category'));
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm('Delete this income source? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/income-sources/${sourceId}`);
      toast.success('Income source deleted');
      await fetchDropdownData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Cannot delete - this source is in use'));
    }
  };

  const handleDeleteIncomeCategory = async (categoryId) => {
    if (!window.confirm('Delete this income category? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/income-categories/${categoryId}`);
      toast.success('Income category deleted');
      await fetchDropdownData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Cannot delete - this category is in use'));
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment method? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/payment-methods/${paymentId}`);
      toast.success('Payment method deleted');
      await fetchDropdownData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Cannot delete - this payment method is in use'));
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Delete this vendor? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/expense-vendors/${vendorId}`);
      toast.success('Vendor deleted');
      await fetchDropdownData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Cannot delete - this vendor is in use'));
    }
  };

  const handleDeleteExpenseCategory = async (categoryId) => {
    if (!window.confirm('Delete this expense category? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/expense-categories/${categoryId}`);
      toast.success('Expense category deleted');
      await fetchDropdownData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Cannot delete - this category is in use'));
    }
  };

  const handleClearAllMappings = async () => {
    if (!window.confirm('Delete ALL Schedule C mappings? This cannot be undone and you will need to recreate all mappings.')) return;
    
    try {
      await axios.delete(`${API}/schedule-c-mappings`);
      toast.success('All mappings deleted successfully');
      // Refresh data after clearing mappings
      await fetchDropdownData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete mappings'));
    }
  };

  const fetchSummary = async () => {
    try {
      const year = parseInt(selectedTaxYear);
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;
      const response = await axios.get(`${API}/summary?period=taxyear&start_date=${startDate}&end_date=${endDate}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary');
    }
  };

  const fetchRecentEntries = async () => {
    try {
      const year = parseInt(selectedTaxYear);
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;
      
      const [incomeRes, expenseRes] = await Promise.all([
        axios.get(`${API}/income?start_date=${startDate}&end_date=${endDate}`),
        axios.get(`${API}/expenses?start_date=${startDate}&end_date=${endDate}`)
      ]);
      
      // Get last 5 entries, sorted by date then by created_at (most recent first)
      const sortedIncome = incomeRes.data
        .sort((a, b) => {
          const dateCompare = new Date(b.date) - new Date(a.date);
          if (dateCompare !== 0) return dateCompare;
          return new Date(b.created_at) - new Date(a.created_at);
        })
        .slice(0, 5);
      
      const sortedExpenses = expenseRes.data
        .filter(e => e.category !== 'Tax Deposit')
        .sort((a, b) => {
          const dateCompare = new Date(b.date) - new Date(a.date);
          if (dateCompare !== 0) return dateCompare;
          return new Date(b.created_at) - new Date(a.created_at);
        })
        .slice(0, 5);
      
      setRecentIncome(sortedIncome);
      setRecentExpenses(sortedExpenses);
    } catch (error) {
      console.error('Failed to fetch recent entries');
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [sourcesRes, incomeCatRes, paymentRes, vendorsRes, expenseCatRes] = await Promise.all([
        axios.get(`${API}/income-sources`),
        axios.get(`${API}/income-categories`),
        axios.get(`${API}/payment-methods`),
        axios.get(`${API}/expense-vendors`),
        axios.get(`${API}/expense-categories`)
      ]);
      
      setIncomeSources(sourcesRes.data);
      setIncomeCategories(incomeCatRes.data);
      setPaymentMethods(paymentRes.data);
      setExpenseVendors(vendorsRes.data);
      setExpenseCategories(expenseCatRes.data);
    } catch (error) {
      console.error('Failed to fetch dropdown data');
    }
  };

  const handleEditIncome = (income) => {
    setEditingIncome(income);
    setIncomeFormData({
      date: income.date,
      source: income.source,
      category: income.category || '',
      payment_method: income.payment_method || '',
      amount: income.amount.toString(),
      notes: income.notes || ''
    });
    setShowIncomeDialog(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseFormData({
      date: expense.date,
      amount: expense.amount.toString(),
      vendor: expense.vendor || '',
      category: expense.category || '',
      notes: expense.notes || ''
    });
    setShowExpenseDialog(true);
  };

  const handleDeleteIncome = (incomeId) => {
    if (!canEdit) {
      toast.error('You do not have permission to delete income');
      return;
    }
    
    setDeleteTarget({ type: 'income', id: incomeId });
    setShowDeleteDialog(true);
  };

  const handleDeleteExpense = (expenseId) => {
    if (!canEdit) {
      toast.error('You do not have permission to delete expense');
      return;
    }
    
    setDeleteTarget({ type: 'expense', id: expenseId });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      if (deleteTarget.type === 'income') {
        await axios.delete(`${API}/income/${deleteTarget.id}`);
        toast.success('Income deleted successfully');
      } else {
        await axios.delete(`${API}/expenses/${deleteTarget.id}`);
        toast.success('Expense deleted successfully');
      }
      
      fetchSummary();
      fetchRecentEntries();
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    } catch (error) {
      toast.error(`Failed to delete ${deleteTarget.type}`);
    }
  };

  const receiptCache = useRef({});

  const handleViewReceipt = async (entryType, fileId) => {
    try {
      setShowReceiptDialog(true);
      
      // Check cache first
      if (receiptCache.current[fileId]) {
        setCurrentReceipt(receiptCache.current[fileId]);
        setReceiptLoading(false);
        return;
      }
      
      setReceiptLoading(true);
      const response = await axios.get(`${API}/download/${fileId}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const imageUrl = URL.createObjectURL(response.data);
      receiptCache.current[fileId] = imageUrl;
      setCurrentReceipt(imageUrl);
      setReceiptLoading(false);
    } catch (error) {
      toast.error('Failed to load receipt');
      setShowReceiptDialog(false);
      setReceiptLoading(false);
    }
  };

  // Resize image to max dimensions while maintaining aspect ratio
  const resizeImage = async (file, maxWidth = 1280, maxHeight = 1280, quality = 0.6) => {
    return new Promise((resolve, reject) => {
      // If file is not an image, return as-is
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = height * (maxWidth / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = width * (maxHeight / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert canvas to blob
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                // Fallback: return original file if blob conversion fails
                console.warn('Canvas toBlob returned null, using original file');
                resolve(file);
                return;
              }
              const resizedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              console.log(`Image resized: ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(resizedFile.size / 1024 / 1024).toFixed(2)}MB`);
              resolve(resizedFile);
            },
            'image/jpeg',
            quality
          );
        };

        img.onerror = () => {
          // If image can't be loaded (e.g. unsupported format), return original
          console.warn('Image load failed, using original file');
          resolve(file);
        };
      };

      reader.onerror = () => {
        // If file can't be read, return original
        console.warn('FileReader failed, using original file');
        resolve(file);
      };
    });
  };

  const handleIncomeSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      toast.error('You do not have permission to edit income');
      return;
    }

    try {
      let response;
      if (editingIncome) {
        // Update existing income
        response = await axios.put(`${API}/income/${editingIncome.id}`, incomeFormData);
        toast.success('Income updated successfully');
      } else {
        // Create new income
        response = await axios.post(`${API}/income`, incomeFormData);
        toast.success('Income added successfully');
      }
      
      // Upload file if selected (separate try-catch to not fail the whole operation)
      if (incomeFile && response.data.id) {
        try {
          // Auto-resize image before upload
          let fileToUpload = incomeFile;
          if (incomeFile.type.startsWith('image/')) {
            toast.info('Optimizing image...');
            fileToUpload = await resizeImage(incomeFile);
          }

          const formData = new FormData();
          formData.append('file', fileToUpload);
          await axios.post(`${API}/upload/income/${response.data.id}`, formData, {
            headers: { 
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          toast.success('Receipt uploaded successfully');
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
          const msg = uploadError?.response?.data?.detail || uploadError?.message || 'Unknown error';
          toast.error(`Income saved, but receipt upload failed: ${msg}`);
        }
      }

      setShowIncomeDialog(false);
      setIncomeFile(null);
      setEditingIncome(null);
      setIncomeFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        source: '',
        category: '',
        payment_method: '',
        amount: '',
        notes: ''
      });
      fetchSummary();
      fetchRecentEntries();
    } catch (error) {
      console.error('Income submit error:', error);
      toast.error(`Failed to ${editingIncome ? 'update' : 'add'} income`);
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      toast.error('You do not have permission to edit expense');
      return;
    }

    try {
      let response;
      if (editingExpense) {
        // Update existing expense
        response = await axios.put(`${API}/expenses/${editingExpense.id}`, expenseFormData);
        toast.success('Expense updated successfully');
      } else {
        // Create new expense
        response = await axios.post(`${API}/expenses`, expenseFormData);
        toast.success('Expense added successfully');
      }
      
      // Upload file if selected (separate try-catch to not fail the whole operation)
      if (expenseFile && response.data.id) {
        try {
          // Auto-resize image before upload
          let fileToUpload = expenseFile;
          if (expenseFile.type.startsWith('image/')) {
            toast.info('Optimizing image...');
            fileToUpload = await resizeImage(expenseFile);
          }

          const formData = new FormData();
          formData.append('file', fileToUpload);
          await axios.post(`${API}/upload/expense/${response.data.id}`, formData, {
            headers: { 
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          toast.success('Receipt uploaded successfully');
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
          const msg = uploadError?.response?.data?.detail || uploadError?.message || 'Unknown error';
          toast.error(`Expense saved, but receipt upload failed: ${msg}`);
        }
      }

      setShowExpenseDialog(false);
      setExpenseFile(null);
      setEditingExpense(null);
      setExpenseFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        vendor: '',
        category: '',
        amount: '',
        notes: ''
      });
      fetchSummary();
      fetchRecentEntries();
    } catch (error) {
      console.error('Expense submit error:', error);
      toast.error(`Failed to ${editingExpense ? 'update' : 'add'} expense`);
    }
  };

  const addExpenseRow = () => {
    setIncomeWithExpensesData({
      ...incomeWithExpensesData,
      expenses: [
        ...incomeWithExpensesData.expenses,
        { vendor: '', category: '', amount: '', notes: '' }
      ]
    });
  };

  const removeExpenseRow = (index) => {
    const newExpenses = incomeWithExpensesData.expenses.filter((_, i) => i !== index);
    setIncomeWithExpensesData({
      ...incomeWithExpensesData,
      expenses: newExpenses
    });
  };

  const updateExpenseRow = (index, field, value) => {
    const newExpenses = [...incomeWithExpensesData.expenses];
    newExpenses[index][field] = value;
    setIncomeWithExpensesData({
      ...incomeWithExpensesData,
      expenses: newExpenses
    });
  };

  const calculateNetIncome = () => {
    const grossIncome = parseFloat(incomeWithExpensesData.income_amount) || 0;
    const totalExpenses = incomeWithExpensesData.expenses.reduce((sum, exp) => {
      return sum + (parseFloat(exp.amount) || 0);
    }, 0);
    return grossIncome - totalExpenses;
  };

  const handleIncomeWithExpensesSubmit = async (e) => {
    e.preventDefault();

    if (!canEdit) {
      toast.error('You do not have permission to add income or expenses');
      return;
    }

    // Validation
    if (!incomeWithExpensesData.source) {
      toast.error('Please select an income source');
      return;
    }

    if (!incomeWithExpensesData.income_amount || parseFloat(incomeWithExpensesData.income_amount) <= 0) {
      toast.error('Please enter a valid income amount');
      return;
    }

    if (incomeWithExpensesData.expenses.length === 0) {
      toast.error('Please add at least one associated expense');
      return;
    }

    // Validate each expense
    for (let i = 0; i < incomeWithExpensesData.expenses.length; i++) {
      const exp = incomeWithExpensesData.expenses[i];
      if (!exp.vendor) {
        toast.error(`Please enter vendor for expense ${i + 1}`);
        return;
      }
      if (!exp.amount || parseFloat(exp.amount) <= 0) {
        toast.error(`Please enter valid amount for expense ${i + 1}`);
        return;
      }
    }

    try {
      const response = await axios.post(`${API}/income-with-expenses`, incomeWithExpensesData);
      toast.success(response.data.message || 'Income and expenses created successfully');
      
      setShowIncomeWithExpensesDialog(false);
      setIncomeWithExpensesData({
        date: format(new Date(), 'yyyy-MM-dd'),
        source: '',
        category: '',
        payment_method: '',
        income_amount: '',
        income_notes: '',
        expenses: []
      });
      fetchSummary();
      fetchRecentEntries();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create income with expenses'));
    }
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="home-panel">
      {/* Header with Tax Year Selector */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 px-4 md:px-0">
        <div className="text-center md:text-left flex-1">
          <h1 className="text-2xl md:text-4xl font-bold text-white">
            Welcome Back{user?.display_name ? `, ${user.display_name}` : ''}!
          </h1>
          <p className="hidden md:block text-sm md:text-base text-slate-400 mt-1 md:mt-2">Quick financial management at your fingertips</p>
        </div>
        
        <div className="flex items-center justify-center md:justify-end gap-3">
          {/* Tax Year Selector */}
          <div className="flex items-center gap-2 bg-slate-800/50 border border-slate-700 rounded-lg p-2 shadow-sm">
            <Calendar className="w-4 h-4 text-cyan-400" />
            <Label className="text-xs font-medium text-slate-300">Tax Year:</Label>
            <Select value={selectedTaxYear} onValueChange={(value) => setSelectedTaxYear(value)}>
              <SelectTrigger className="w-24 h-8 text-sm bg-slate-800 border-slate-600 text-white" data-testid="home-tax-year-selector">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {availableTaxYears.map(year => (
                  <SelectItem key={year} value={year} className="text-white hover:bg-slate-700">{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Hide Data button only shows on desktop - moved to Financial Summary on mobile */}
          <Button
            variant="ghost"
            onClick={toggleHideAmounts}
            className="hidden md:flex items-center gap-2 bg-slate-800/50 hover:bg-slate-700 px-3 h-9 border border-slate-700"
            data-testid="toggle-amounts-visibility"
            title={hideAmounts ? "Show amounts" : "Hide amounts"}
          >
            {hideAmounts ? (
              <>
                <EyeOff className="w-4 h-4 text-slate-300" />
                <span className="text-xs font-medium text-slate-300">Show Data</span>
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 text-cyan-400" />
                <span className="text-xs font-medium text-cyan-400">Hide Data</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Quick Actions - Mobile at top, Desktop below summary */}
      {canEdit && (
        <div className="md:hidden grid grid-cols-3 gap-2 px-4">
          <Button
            onClick={() => setShowIncomeDialog(true)}
            className="h-20 flex flex-col gap-1 bg-gradient-to-br from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg"
            data-testid="quick-add-income"
          >
            <Plus className="w-6 h-6" />
            <span className="text-[10px] font-semibold leading-tight">Add Income</span>
          </Button>
          <Button
            onClick={() => setShowExpenseDialog(true)}
            className="h-20 flex flex-col gap-1 bg-gradient-to-br from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white shadow-lg"
            data-testid="quick-add-expense"
          >
            <Plus className="w-6 h-6" />
            <span className="text-[10px] font-semibold leading-tight">Add Expense</span>
          </Button>
          <Button
            onClick={() => setShowIncomeWithExpensesDialog(true)}
            className="h-20 flex flex-col gap-1 bg-gradient-to-br from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white shadow-lg"
            data-testid="quick-add-income-with-expenses"
          >
            <Plus className="w-6 h-6" />
            <span className="text-[10px] font-semibold text-center leading-tight">Income + Expenses</span>
          </Button>
        </div>
      )}

      {/* Mileage Tracker - Mobile Only */}
      {canEdit && (
        <div className="md:hidden px-4">
          <MileageTracker onExpenseAdded={() => { fetchSummary(); fetchRecentEntries(); }} />
        </div>
      )}

      {/* Summary Cards - Combined on Mobile, Separate on Desktop */}
      <div className="px-4 md:px-0">
        {/* Mobile: Single Combined Card */}
        <Card className="border-0 shadow-lg md:hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div 
                className="flex items-center gap-2 flex-1 cursor-pointer hover:bg-slate-50 transition-colors py-1 -my-1 rounded"
                onClick={() => setShowFinancialSummary(!showFinancialSummary)}
              >
                <CardTitle className="text-base md:text-lg min-w-0 flex-1">Financial Summary</CardTitle>
                <div className="flex-shrink-0 flex items-center justify-center w-6">
                  {showFinancialSummary ? (
                    <ChevronUp className="w-5 h-5 text-slate-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-600" />
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHideAmounts();
                }}
                className="md:hidden flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 px-2 h-8"
                title={hideAmounts ? "Show amounts" : "Hide amounts"}
              >
                {hideAmounts ? (
                  <>
                    <EyeOff className="w-4 h-4 text-emerald-700" />
                    <span className="text-xs font-medium text-emerald-700">Show</span>
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 text-emerald-700" />
                    <span className="text-xs font-medium text-emerald-700">Hide</span>
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          {showFinancialSummary && (
            <CardContent className="space-y-3">
            {/* Total Income */}
            <div className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-800">Total Income</span>
              </div>
              <div className="text-sm font-semibold text-emerald-600">
                {formatAmount(summary.total_income)}
              </div>
            </div>

            {/* Total Expenses */}
            <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-600" />
                <span className="text-sm font-medium text-red-800">Total Expenses</span>
              </div>
              <div className="text-sm font-semibold text-red-600">
                {formatAmount(summary.total_expenses)}
              </div>
            </div>

            {/* Net Profit */}
            <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Net Profit</span>
              </div>
              <div className={`text-sm font-semibold ${summary.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                {formatAmount(summary.profit)}
              </div>
            </div>

            {/* Taxes Paid */}
            <div className="flex items-center justify-between p-2 bg-indigo-50 rounded-lg">
              <div className="flex items-center gap-2">
                <BadgeDollarSign className="w-5 h-5 text-indigo-600" />
                <span className="text-sm font-medium text-indigo-800">Taxes Paid</span>
              </div>
              <div className="text-sm font-semibold text-indigo-600">
                {formatAmount(summary.taxes_paid || 0)}
              </div>
            </div>

            {/* Taxes Owed */}
            <div className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">Taxes Owed</span>
              </div>
              <div className={`text-sm font-semibold ${(summary.estimated_tax || 0) > 0 ? 'text-purple-600' : 'text-green-600'}`}>
                {formatAmount(summary.estimated_tax || 0)}
              </div>
            </div>
          </CardContent>
          )}
        </Card>

        {/* Desktop: Five Separate Cards */}
        <div className="hidden md:grid grid-cols-5 gap-3" data-testid="home-summary-cards">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-green-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Income</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-7 h-7 text-emerald-600" />
                <div className="text-2xl font-bold text-slate-900">
                  {formatAmount(summary.total_income)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-orange-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Expenses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <TrendingDown className="w-7 h-7 text-red-600" />
                <div className="text-2xl font-bold text-slate-900">
                  {formatAmount(summary.total_expenses)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Net Profit</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Wallet className="w-7 h-7 text-blue-600" />
                <div className="text-2xl font-bold text-slate-900">
                  {formatAmount(summary.profit)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-indigo-50 to-blue-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Taxes Paid</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <BadgeDollarSign className="w-7 h-7 text-indigo-600" />
                <div className="text-2xl font-bold text-slate-900">
                  {formatAmount(summary.taxes_paid || 0)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Taxes Owed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Receipt className="w-7 h-7 text-purple-600" />
                <div className={`text-2xl font-bold ${(summary.estimated_tax || 0) > 0 ? 'text-slate-900' : 'text-green-700'}`}>
                  {formatAmount(summary.estimated_tax || 0)}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Actions - Desktop Only (Mobile buttons are at top) */}
      {canEdit && (
        <div className="hidden md:grid grid-cols-3 gap-4 px-4 md:px-0">
          <Button
            onClick={() => setShowIncomeDialog(true)}
            className="h-32 flex flex-col gap-1 bg-gradient-to-br from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg"
            data-testid="quick-add-income"
          >
            <Plus className="w-12 h-12" />
            <span className="text-xl font-semibold leading-tight">Add Income</span>
          </Button>
          <Button
            onClick={() => setShowExpenseDialog(true)}
            className="h-32 flex flex-col gap-1 bg-gradient-to-br from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white shadow-lg"
            data-testid="quick-add-expense"
          >
            <Plus className="w-12 h-12" />
            <span className="text-xl font-semibold leading-tight">Add Expense</span>
          </Button>
          <Button
            onClick={() => setShowIncomeWithExpensesDialog(true)}
            className="h-32 flex flex-col gap-1 bg-gradient-to-br from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white shadow-lg"
            data-testid="quick-add-income-with-expenses"
          >
            <Plus className="w-12 h-12" />
            <span className="text-base font-semibold text-center leading-tight">Income + Expenses</span>
          </Button>
        </div>
      )}

      {/* Recent Entries */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 px-4 md:px-0">
        {/* Recent Income */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div 
                className="flex items-center gap-2 flex-1 cursor-pointer hover:bg-slate-50 transition-colors py-1 -my-1 rounded"
                onClick={() => setShowRecentIncome(!showRecentIncome)}
              >
                <CardTitle className="text-base md:text-lg min-w-0 flex-1">Recent Income</CardTitle>
                <div className="flex-shrink-0 flex items-center justify-center w-6">
                  {showRecentIncome ? (
                    <ChevronUp className="w-5 h-5 text-slate-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-600" />
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHideRecentIncomeAmounts();
                }}
                className="flex items-center gap-1.5 bg-emerald-100 hover:bg-emerald-200 px-2 h-8"
                title={hideRecentIncomeAmounts ? "Show amounts" : "Hide amounts"}
              >
                {hideRecentIncomeAmounts ? (
                  <>
                    <EyeOff className="w-4 h-4 text-emerald-700" />
                    <span className="text-xs font-medium text-emerald-700">Show</span>
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 text-emerald-700" />
                    <span className="text-xs font-medium text-emerald-700">Hide</span>
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          {showRecentIncome && (
            <CardContent>
              {recentIncome.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No income entries yet</p>
              ) : (
                <div className="space-y-2 md:space-y-3">
                  {recentIncome.map((income) => (
                    <div key={income.id} className="flex justify-between items-center p-2 md:p-3 bg-emerald-50 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm md:text-base font-medium text-emerald-900 truncate">{income.source}</p>
                          {income.receipt_file_id && (
                            <button
                              onClick={() => handleViewReceipt('income', income.receipt_file_id)}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="View receipt"
                            >
                              <FileText className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-emerald-700">{income.date}</p>
                      </div>
                      <div className="flex items-center gap-2 md:gap-3 ml-3">
                        <p className="text-sm md:text-base font-semibold text-emerald-600 whitespace-nowrap">
                          +{formatAmount(income.amount, hideRecentIncomeAmounts)}
                        </p>
                        {canEdit && (
                          <>
                            <button
                              onClick={() => handleEditIncome(income)}
                              className="text-slate-400 hover:text-blue-600 transition-colors"
                              title="Edit income"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteIncome(income.id)}
                              className="text-slate-400 hover:text-red-600 transition-colors"
                              title="Delete income"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Recent Expenses */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div 
                className="flex items-center gap-2 flex-1 cursor-pointer hover:bg-slate-50 transition-colors py-1 -my-1 rounded"
                onClick={() => setShowRecentExpenses(!showRecentExpenses)}
              >
                <CardTitle className="text-base md:text-lg min-w-0 flex-1">Recent Expenses</CardTitle>
                <div className="flex-shrink-0 flex items-center justify-center w-6">
                  {showRecentExpenses ? (
                    <ChevronUp className="w-5 h-5 text-slate-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-600" />
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHideRecentExpenseAmounts();
                }}
                className="flex items-center gap-1.5 bg-red-100 hover:bg-red-200 px-2 h-8"
                title={hideRecentExpenseAmounts ? "Show amounts" : "Hide amounts"}
              >
                {hideRecentExpenseAmounts ? (
                  <>
                    <EyeOff className="w-4 h-4 text-red-700" />
                    <span className="text-xs font-medium text-red-700">Show</span>
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 text-red-700" />
                    <span className="text-xs font-medium text-red-700">Hide</span>
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          {showRecentExpenses && (
            <CardContent>
              {recentExpenses.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No expense entries yet</p>
              ) : (
                <div className="space-y-2 md:space-y-3">
                  {recentExpenses.map((expense) => {
                    const isTaxDeposit = expense.category === "Tax Deposits";
                    return (
                    <div key={expense.id} className={`flex justify-between items-center p-2 md:p-3 rounded-lg ${isTaxDeposit ? 'bg-green-50 border border-green-200' : 'bg-red-50'}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm md:text-base font-medium truncate ${isTaxDeposit ? 'text-green-900' : 'text-red-900'}`}>{expense.vendor}</p>
                          {isTaxDeposit && (
                            <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Tax Payment</span>
                          )}
                          {expense.receipt_file_id && (
                            <button
                              onClick={() => handleViewReceipt('expense', expense.receipt_file_id)}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="View receipt"
                            >
                              <FileText className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <p className={`text-xs ${isTaxDeposit ? 'text-green-700' : 'text-red-700'}`}>{expense.date}</p>
                      </div>
                      <div className="flex items-center gap-2 md:gap-3 ml-3">
                        <p className={`text-sm md:text-base font-semibold whitespace-nowrap ${isTaxDeposit ? 'text-green-600' : 'text-red-600'}`}>
                          -{formatAmount(expense.amount, hideRecentExpenseAmounts)}
                        </p>
                        {canEdit && (
                          <>
                            <button
                              onClick={() => handleEditExpense(expense)}
                              className={`text-slate-400 transition-colors ${isTaxDeposit ? 'hover:text-green-600' : 'hover:text-red-600'}`}
                              title="Edit expense"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteExpense(expense.id)}
                              className={`text-slate-400 transition-colors ${isTaxDeposit ? 'hover:text-green-600' : 'hover:text-red-600'}`}
                              title="Delete expense"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )})}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Charts Section - DISABLED */}
        {false && (
          <div className="mt-6 space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <CardTitle className="text-base md:text-lg">Income & Expense Trends</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant={chartPeriod === 'week' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setChartPeriod('week')}
                    >
                      Week
                    </Button>
                    <Button
                      variant={chartPeriod === 'month' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setChartPeriod('month')}
                    >
                      Month
                    </Button>
                    <Button
                      variant={chartPeriod === 'year' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setChartPeriod('year')}
                    >
                      Year
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                      <Legend />
                      <Bar dataKey="income" fill="#10b981" name="Income" />
                      <Bar dataKey="expenses" fill="#ef4444" name="Expenses" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {topIncomeCategories && topIncomeCategories.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-base md:text-lg">Top Income Sources</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topIncomeCategories} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="category" type="category" width={150} />
                        <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                        <Bar dataKey="amount" fill="#10b981" name="Amount" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {topExpenseCategories && topExpenseCategories.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-base md:text-lg">Top Expense Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={topExpenseCategories} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="category" type="category" width={150} />
                        <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                        <Bar dataKey="amount" fill="#ef4444" name="Amount" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Income Dialog */}
      <Dialog open={showIncomeDialog} onOpenChange={setShowIncomeDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="quick-income-dialog">
          <DialogHeader>
            <DialogTitle>{editingIncome ? 'Edit Income' : 'Add Income'}</DialogTitle>
            <DialogDescription>Enter income details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleIncomeSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="income-date">Date</Label>
              <Input
                id="income-date"
                type="date"
                value={incomeFormData.date}
                onChange={(e) => setIncomeFormData({ ...incomeFormData, date: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="income-category">Customer/Source (Optional)</Label>
              {showManageIncomeCategories ? (
                <div className="space-y-2">
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {incomeCategories.map((cat) => (
                      <div key={cat.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                        <span className="text-sm">{cat.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteIncomeCategory(cat.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowManageIncomeCategories(false)} className="w-full">
                    Done
                  </Button>
                </div>
              ) : (
                <Select
                  value={incomeFormData.category}
                  onValueChange={(value) => {
                    if (value === '__add_new__') {
                      setShowAddIncomeCategoryDialog(true);
                    } else if (value === '__manage__') {
                      setShowManageIncomeCategories(true);
                    } else {
                      // Find the selected customer and auto-populate their source
                      const selectedCustomer = incomeCategories.find(c => c.name === value);
                      setIncomeFormData({ 
                        ...incomeFormData, 
                        category: value,
                        source: selectedCustomer?.source || incomeFormData.source
                      });
                    }
                  }}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select customer/source (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {incomeCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__add_new__" className="text-blue-600 font-medium">
                      + Add New Customer/Source
                    </SelectItem>
                    <SelectItem value="__manage__" className="text-slate-600 font-medium">
                      ⚙ Manage Customers/Sources
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="income-source">Type of Income</Label>
              <Select
                value={incomeFormData.source}
                onValueChange={(value) => {
                  setIncomeFormData({ ...incomeFormData, source: value });
                }}
                required
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select type of income" />
                </SelectTrigger>
                <SelectContent>
                  {incomeSources.map((source) => (
                    <SelectItem key={source.id} value={source.name}>
                      {source.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="income-payment">Payment (Optional)</Label>
              {showAddPayment ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter new payment method"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    className="h-11"
                    autoFocus
                  />
                  <Button type="button" size="sm" onClick={handleAddPayment} className="h-11">
                    Add
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => {setShowAddPayment(false); setNewItemName('');}} className="h-11">
                    Cancel
                  </Button>
                </div>
              ) : showManagePayments ? (
                <div className="space-y-2">
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {paymentMethods.map((method) => (
                      <div key={method.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                        <span className="text-sm">{method.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePayment(method.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowManagePayments(false)} className="w-full">
                    Done
                  </Button>
                </div>
              ) : (
                <Select
                  value={incomeFormData.payment_method}
                  onValueChange={(value) => {
                    setIncomeFormData({ ...incomeFormData, payment_method: value });
                  }}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map((method) => (
                      <SelectItem key={method.id} value={method.name}>
                        {method.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="income-amount">Amount</Label>
              <Input
                id="income-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={incomeFormData.amount}
                onChange={(e) => setIncomeFormData({ ...incomeFormData, amount: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="income-notes">Notes (Optional)</Label>
              <Textarea
                id="income-notes"
                placeholder="Additional notes..."
                value={incomeFormData.notes}
                onChange={(e) => setIncomeFormData({ ...incomeFormData, notes: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Receipt (Optional)</Label>
              {editingIncome && editingIncome.receipt_file_id && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleViewReceipt('income', editingIncome.receipt_file_id)}
                  className="w-full h-11 mb-2"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Current Receipt
                </Button>
              )}
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setIncomeFile(e.target.files[0])}
                  className="flex-1 h-11"
                />
                <label htmlFor="income-camera">
                  <input
                    id="income-camera"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => setIncomeFile(e.target.files[0])}
                    className="hidden"
                  />
                  <Button type="button" variant="outline" size="icon" as="span" className="h-11 w-11">
                    <Camera className="w-5 h-5" />
                  </Button>
                </label>
              </div>
              {incomeFile && (
                <p className="text-xs text-slate-600">
                  Selected: {incomeFile.name} ({(incomeFile.size / 1024 / 1024).toFixed(2)} MB)
                  {incomeFile.type.startsWith('image/') && ' - Will be optimized automatically'}
                </p>
              )}
              {editingIncome && editingIncome.receipt_file_id && !incomeFile && (
                <p className="text-xs text-blue-600">Receipt already attached. Upload new file to replace.</p>
              )}
              {!incomeFile && !editingIncome?.receipt_file_id && (
                <p className="text-xs text-slate-500">Images are automatically optimized for upload</p>
              )}
            </div>
            <Button type="submit" className="w-full h-11 text-base">{editingIncome ? 'Update Income' : 'Add Income'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="quick-expense-dialog">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
            <DialogDescription>Enter expense details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleExpenseSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="expense-date">Date</Label>
              <Input
                id="expense-date"
                type="date"
                value={expenseFormData.date}
                onChange={(e) => setExpenseFormData({ ...expenseFormData, date: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-vendor">Vendor (Optional)</Label>
              {showAddVendor ? (
                <div className="space-y-2">
                  <Input
                    placeholder="Enter vendor name"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    className="h-11"
                    autoFocus
                  />
                  <Select
                    value={newVendorCategory}
                    onValueChange={setNewVendorCategory}
                    required
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select category (required)" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={handleAddVendor} className="h-11 flex-1">
                      Add Vendor
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => {setShowAddVendor(false); setNewItemName(''); setNewVendorCategory('');}} className="h-11">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : showManageVendors ? (
                <div className="space-y-2">
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {expenseVendors.map((vendor) => (
                      <div key={vendor.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{vendor.name}</span>
                          <span className="text-xs text-slate-500">Category: {vendor.category}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteVendor(vendor.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowManageVendors(false)} className="w-full">
                    Done
                  </Button>
                </div>
              ) : (
                <Select
                  value={expenseFormData.vendor}
                  onValueChange={(value) => {
                    if (value === '__add_new__') {
                      setShowAddVendor(true);
                    } else if (value === '__manage__') {
                      setShowManageVendors(true);
                    } else {
                      // Find the selected vendor and auto-populate its category
                      const selectedVendor = expenseVendors.find(v => v.name === value);
                      setExpenseFormData({ 
                        ...expenseFormData, 
                        vendor: value,
                        category: selectedVendor?.category || expenseFormData.category
                      });
                    }
                  }}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseVendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.name}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="__add_new__" className="text-blue-600 font-medium">
                      + Add New Vendor
                    </SelectItem>
                    <SelectItem value="__manage__" className="text-slate-600 font-medium">
                      ⚙ Manage Vendors
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-category">Category</Label>
              {showAddExpenseCategory ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter new category"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    className="h-11"
                    autoFocus
                  />
                  <Button type="button" size="sm" onClick={handleAddExpenseCategory} className="h-11">
                    Add
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => {setShowAddExpenseCategory(false); setNewItemName('');}} className="h-11">
                    Cancel
                  </Button>
                </div>
              ) : showManageExpenseCategories ? (
                <div className="space-y-2">
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {expenseCategories.map((cat) => (
                      <div key={cat.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                        <span className="text-sm">{cat.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteExpenseCategory(cat.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => setShowManageExpenseCategories(false)} className="w-full">
                    Done
                  </Button>
                </div>
              ) : (
                <Select
                  value={expenseFormData.category}
                  onValueChange={(value) => {
                    setExpenseFormData({ ...expenseFormData, category: value });
                  }}
                  required
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-amount">Amount</Label>
              <Input
                id="expense-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={expenseFormData.amount}
                onChange={(e) => setExpenseFormData({ ...expenseFormData, amount: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expense-notes">Notes (Optional)</Label>
              <Textarea
                id="expense-notes"
                placeholder="Additional notes..."
                value={expenseFormData.notes}
                onChange={(e) => setExpenseFormData({ ...expenseFormData, notes: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Receipt (Optional)</Label>
              {editingExpense && editingExpense.receipt_file_id && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleViewReceipt('expense', editingExpense.receipt_file_id)}
                  className="w-full h-11 mb-2"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Current Receipt
                </Button>
              )}
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setExpenseFile(e.target.files[0])}
                  className="flex-1 h-11"
                />
                <label htmlFor="expense-camera">
                  <input
                    id="expense-camera"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => setExpenseFile(e.target.files[0])}
                    className="hidden"
                  />
                  <Button type="button" variant="outline" size="icon" as="span" className="h-11 w-11">
                    <Camera className="w-5 h-5" />
                  </Button>
                </label>
              </div>
              {expenseFile && (
                <p className="text-xs text-slate-600">
                  Selected: {expenseFile.name} ({(expenseFile.size / 1024 / 1024).toFixed(2)} MB)
                  {expenseFile.type.startsWith('image/') && ' - Will be optimized automatically'}
                </p>
              )}
              {editingExpense && editingExpense.receipt_file_id && !expenseFile && (
                <p className="text-xs text-blue-600">Receipt already attached. Upload new file to replace.</p>
              )}
              {!expenseFile && !editingExpense?.receipt_file_id && (
                <p className="text-xs text-slate-500">Images are automatically optimized for upload</p>
              )}
            </div>
            <Button type="submit" className="w-full h-11 text-base">{editingExpense ? 'Update Expense' : 'Add Expense'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Income with Expenses Dialog */}
      <Dialog open={showIncomeWithExpensesDialog} onOpenChange={setShowIncomeWithExpensesDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Income with Associated Expenses</DialogTitle>
            <DialogDescription>
              Record gross income and expenses in a single transaction (e.g., $10,000 earned - $2,000 club fee - $1,000 tip out)
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleIncomeWithExpensesSubmit} className="space-y-6">
            {/* Income Section */}
            <div className="border-2 border-emerald-200 rounded-lg p-4 bg-emerald-50">
              <h3 className="text-lg font-semibold mb-4 text-emerald-900">Income Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={incomeWithExpensesData.date}
                    onChange={(e) => setIncomeWithExpensesData({ ...incomeWithExpensesData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Customer/Source (Optional)</Label>
                  <Select
                    value={incomeWithExpensesData.category}
                    onValueChange={(value) => {
                      if (value === '__add_new__') {
                        setShowAddIncomeCategoryDialog(true);
                      } else {
                        // Find the selected customer and auto-populate their source
                        const selectedCustomer = incomeCategories.find(c => c.name === value);
                        setIncomeWithExpensesData({ 
                          ...incomeWithExpensesData, 
                          category: value,
                          source: selectedCustomer?.source || incomeWithExpensesData.source
                        });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select customer/source (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {incomeCategories.map((category) => (
                        <SelectItem key={category.id} value={category.name}>
                          {category.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__add_new__" className="text-blue-600 font-medium">
                        + Add New Customer/Source
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type of Income</Label>
                  <Select
                    value={incomeWithExpensesData.source}
                    onValueChange={(value) => setIncomeWithExpensesData({ ...incomeWithExpensesData, source: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type of income" />
                    </SelectTrigger>
                    <SelectContent>
                      {incomeSources.map((source) => (
                        <SelectItem key={source.id} value={source.name}>
                          {source.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Method (Optional)</Label>
                  <Select
                    value={incomeWithExpensesData.payment_method}
                    onValueChange={(value) => setIncomeWithExpensesData({ ...incomeWithExpensesData, payment_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select payment method" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentMethods.map((method) => (
                        <SelectItem key={method.id} value={method.name}>
                          {method.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Gross Income Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="10000.00"
                    value={incomeWithExpensesData.income_amount}
                    onChange={(e) => setIncomeWithExpensesData({ ...incomeWithExpensesData, income_amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Input
                    placeholder="Additional notes..."
                    value={incomeWithExpensesData.income_notes}
                    onChange={(e) => setIncomeWithExpensesData({ ...incomeWithExpensesData, income_notes: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Associated Expenses Section */}
            <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-red-900">Associated Expenses</h3>
                <Button
                  type="button"
                  onClick={addExpenseRow}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Expense
                </Button>
              </div>
              
              {incomeWithExpensesData.expenses.length === 0 ? (
                <p className="text-sm text-slate-600 text-center py-4">
                  No expenses added yet. Click "Add Expense" to add associated expenses.
                </p>
              ) : (
                <div className="space-y-3">
                  {incomeWithExpensesData.expenses.map((expense, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-3 bg-white rounded border">
                      <div className="space-y-1">
                        <Label className="text-xs">Vendor</Label>
                        <Input
                          placeholder="Vendor name"
                          value={expense.vendor}
                          onChange={(e) => updateExpenseRow(index, 'vendor', e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Category</Label>
                        <Select
                          value={expense.category}
                          onValueChange={(value) => updateExpenseRow(index, 'category', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {expenseCategories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.name}>
                                {cat.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Amount</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={expense.amount}
                          onChange={(e) => updateExpenseRow(index, 'amount', e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Notes</Label>
                        <Input
                          placeholder="Notes"
                          value={expense.notes}
                          onChange={(e) => updateExpenseRow(index, 'notes', e.target.value)}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          onClick={() => removeExpenseRow(index)}
                          variant="destructive"
                          size="sm"
                          className="w-full"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Net Income Calculation */}
            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
              <h3 className="text-lg font-semibold mb-3 text-blue-900">Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-700">Gross Income:</span>
                  <span className="font-semibold text-emerald-700">
                    ${(parseFloat(incomeWithExpensesData.income_amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-700">Total Expenses:</span>
                  <span className="font-semibold text-red-700">
                    ${incomeWithExpensesData.expenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t-2 border-blue-300">
                  <span className="text-slate-900">Net Income:</span>
                  <span className={`${calculateNetIncome() >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    ${calculateNetIncome().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full">
              Create Income and Expenses
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Income Category Dialog */}
      <Dialog open={showAddIncomeCategoryDialog} onOpenChange={(open) => {
        setShowAddIncomeCategoryDialog(open);
        if (!open) {
          // Reset form when closing
          setNewIncomeCategoryName('');
          setNewIncomeCategorySource('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Customer/Source</DialogTitle>
            <DialogDescription>Enter customer name and select their revenue category</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Customer/Source Name</Label>
              <Input
                placeholder="Enter customer/source name"
                value={newIncomeCategoryName}
                onChange={(e) => setNewIncomeCategoryName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddIncomeCategory();
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Revenue Category (Required)</Label>
              <Select
                value={newIncomeCategorySource}
                onValueChange={setNewIncomeCategorySource}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select revenue category" />
                </SelectTrigger>
                <SelectContent>
                  {incomeSources.map((source) => (
                    <SelectItem key={source.id} value={source.name}>
                      {source.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAddIncomeCategory} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer/Source
            </Button>
            {incomeCategories.length > 0 && (
              <div className="border-t pt-3">
                <Label className="text-sm font-medium mb-2 block">Existing Items</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {incomeCategories.map((category) => (
                    <div
                      key={category.id}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded hover:bg-slate-100"
                    >
                      <span className="text-sm">{category.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          if (window.confirm(`Delete "${category.name}"?`)) {
                            try {
                              await axios.delete(`${API}/income-categories/${category.id}`, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                              });
                              toast.success('Deleted successfully');
                              fetchDropdownData();
                            } catch (error) {
                              toast.error('Failed to delete');
                            }
                          }
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Viewer Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>View Receipt</DialogTitle>
            <DialogDescription>Receipt image</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center bg-slate-100 rounded-lg p-4 min-h-[400px]">
            {receiptLoading ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-slate-600">Loading receipt...</p>
              </div>
            ) : currentReceipt ? (
              <img 
                src={currentReceipt} 
                alt="Receipt" 
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            ) : (
              <p className="text-slate-500">No receipt available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deleteTarget?.type} entry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete {deleteTarget?.type === 'income' ? 'Income' : 'Expense'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
